/* Restyle header: nav dropdowns, nav fitting, mobile search toggle, rotating placeholder, mobile drawer. */
if (!customElements.get('rs-header')) {
  customElements.define(
    'rs-header',
    class RsHeader extends HTMLElement {
      connectedCallback() {
        this.mobileQuery = window.matchMedia('(max-width: 900px)');
        this.initDropdowns();
        this.initNavFit();
        this.initSearch();
        this.initDrawer();
      }

      /* ---------- Desktop dropdowns ---------- */
      initDropdowns() {
        this.toggles = [...this.querySelectorAll('[data-rs-dd-toggle]')];
        if (!this.toggles.length) return;
        const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        this.toggles.forEach((toggle) => {
          const item = toggle.closest('li');
          toggle.addEventListener('click', () => this.setDropdown(toggle, toggle.getAttribute('aria-expanded') !== 'true'));
          if (canHover) {
            let timer;
            item.addEventListener('mouseenter', () => {
              clearTimeout(timer);
              this.setDropdown(toggle, true);
            });
            item.addEventListener('mouseleave', () => {
              timer = setTimeout(() => this.setDropdown(toggle, false), 120);
            });
          }
        });

        document.addEventListener('click', (event) => {
          if (!this.querySelector('.rs-nav')?.contains(event.target)) this.closeDropdowns();
        });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          const open = this.toggles.find((t) => t.getAttribute('aria-expanded') === 'true');
          if (open) {
            this.setDropdown(open, false);
            open.focus();
          }
        });
        this.addEventListener('focusout', (event) => {
          const nav = this.querySelector('.rs-nav');
          if (nav && !nav.contains(event.relatedTarget)) this.closeDropdowns();
        });
      }

      setDropdown(toggle, open) {
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;
        if (open) this.closeDropdowns(toggle);
        toggle.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
      }

      closeDropdowns(except) {
        (this.toggles || []).forEach((t) => {
          if (t === except) return;
          t.setAttribute('aria-expanded', 'false');
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = true;
        });
      }

      /* ---------- Keep the nav on one line: hide items before the highlight item, from the end ---------- */
      initNavFit() {
        this.navList = this.querySelector('[data-rs-nav]');
        if (!this.navList) return;
        const items = [...this.navList.children];
        const keepIndex = items.findIndex((li) => li.hasAttribute('data-rs-keep'));
        const pool = keepIndex > -1 ? items.slice(1, keepIndex) : items.slice(1, -1);
        this.navCandidates = pool.reverse();
        const fit = () => this.fitNav();
        if ('ResizeObserver' in window) new ResizeObserver(fit).observe(this.navList);
        else window.addEventListener('resize', fit);
        document.fonts?.ready.then(fit);
        fit();
      }

      fitNav() {
        if (!this.navList || this.navList.offsetParent === null) return;
        this.navCandidates.forEach((li) => li.removeAttribute('data-rs-hidden'));
        for (const li of this.navCandidates) {
          if (this.navList.scrollWidth <= this.navList.clientWidth + 1) break;
          li.setAttribute('data-rs-hidden', '');
        }
      }

      /* ---------- Search ---------- */
      initSearch() {
        this.searchToggle = this.querySelector('[data-rs-search-toggle]');
        this.searchInput = this.querySelector('.rs-search input[type="search"]');
        this.searchToggle?.addEventListener('click', () => {
          const open = !this.classList.contains('is-search-open');
          this.classList.toggle('is-search-open', open);
          this.searchToggle.setAttribute('aria-expanded', String(open));
          if (open) this.searchInput?.focus();
        });

        if (!this.searchInput) return;
        const terms = (this.dataset.searchTerms || '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean);
        const prefix = this.dataset.searchPrefix || '';
        const mobile = this.dataset.searchMobile || '';
        let index = 0;
        const setPlaceholder = () => {
          if (this.mobileQuery.matches && mobile) this.searchInput.placeholder = mobile;
          else if (terms.length) this.searchInput.placeholder = prefix + terms[index % terms.length];
        };
        setPlaceholder();
        this.mobileQuery.addEventListener('change', setPlaceholder);
        if (terms.length && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          let text = '';
          let deleting = false;
          const type = () => {
            let delay;
            if (this.mobileQuery.matches || document.activeElement === this.searchInput) {
              delay = 500;
            } else {
              const term = terms[index % terms.length];
              text = deleting ? term.substring(0, text.length - 1) : term.substring(0, text.length + 1);
              this.searchInput.placeholder = prefix + text;
              delay = deleting ? 100 : 150;
              if (!deleting && text === term) {
                deleting = true;
                delay = 1000;
              } else if (deleting && text === '') {
                deleting = false;
                index = (index + 1) % terms.length;
                delay = 500;
              }
            }
            setTimeout(type, delay);
          };
          type();
        }
      }

      /* ---------- Mobile drawer ---------- */
      initDrawer() {
        this.burger = this.querySelector('[data-rs-burger]');
        this.drawer = this.burger ? document.getElementById(this.burger.getAttribute('aria-controls')) : null;
        if (!this.drawer) return;

        this.burger.addEventListener('click', () => this.setDrawer(true));
        this.drawer.querySelectorAll('[data-rs-mm-close]').forEach((el) => el.addEventListener('click', () => this.setDrawer(false)));
        this.drawer.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.setDrawer(false);
          if (event.key === 'Tab') this.trapFocus(event);
        });
        window.matchMedia('(min-width: 901px)').addEventListener('change', (event) => {
          if (event.matches && this.drawer.classList.contains('is-open')) this.setDrawer(false);
        });

        const makeToggle = this.drawer.querySelector('[data-rs-mm-toggle]');
        makeToggle?.addEventListener('click', () => {
          const list = document.getElementById(makeToggle.getAttribute('aria-controls'));
          const open = !list.classList.contains('is-open');
          list.classList.toggle('is-open', open);
          makeToggle.setAttribute('aria-expanded', String(open));
        });
      }

      setDrawer(open) {
        this.drawer.classList.toggle('is-open', open);
        this.drawer.setAttribute('aria-hidden', String(!open));
        this.burger.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('rs-mm-lock', open);
        if (open) this.drawer.querySelector('.rs-mm-x')?.focus();
        else this.burger.focus();
      }

      trapFocus(event) {
        const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), summary, input, select')].filter(
          (el) => el.offsetParent !== null
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
